import parse from 'html-react-parser';
import { renderToStaticMarkup } from 'react-dom/server';
import clozeMathService from '../services/ClozeMathService';
import htmlService from '../services/HtmlService';
import promptService from '../services/PromptService';
import questionService from '../services/QuestionService';
import lessonManager from '../managers/LessonManager';

import { ClozeBehavior } from './ClozeBehavior';

import utilsService from '../services/UtilsService';

export class ClozeComboBehavior extends ClozeBehavior {
  setResponseData = (data, _responseAnswer, model, { dataId, lessonElementState, isCustomBlankSection } = {}) => {
    const prompt = promptService.getCurrentPrompt({
      dataId,
      lessonElementState,
      model,
      promptType: 'anyModelPrompt'
    });

    if (prompt && prompt.rangeType === 'cloze') {
      if (!lessonElementState.currentResponseAnswer.userInputMap) {
        questionService.updateUserInputMap({
          model,
          lessonElementState
        });
      } else if (htmlService.isHtmlResponseFormat(model) || isCustomBlankSection) {
        lessonElementState.currentResponseAnswer.userInputMap.set(dataId, {
          dataId,
          text: data
        });
      } else {
        // `responseFormat` is 'text' or 'numeric' and using the native input element
        if (model.responseFormat === 'numeric') {
          if (utilsService.ensureNumericInput(data.target.value)) {
            lessonElementState.currentResponseAnswer.userInputMap.set(dataId, {
              dataId,
              text: data?.target?.value
            });
          }
        } else {
          lessonElementState.currentResponseAnswer.userInputMap.set(dataId, {
            dataId,
            text: data?.target?.value
          });
        }
      }
    } else if (promptService.isMultiSelectDropdownPrompt({ dataId, model }) || Array.isArray(data)) {
      const selectedOptions = (Array.isArray(data) ? data : []).filter((option) => (
        option && option.dataId !== 'prependedSelectLabel' && option.value !== 'prependedSelectLabel'
      ));
      const selections = selectedOptions.map((option) => ({
        answerId: questionService.getPickAnswerId(option) || undefined,
        text: (model?.responseFormat === 'html') ? option.viewLabel : option.label
      }));
      const payload = {
        dataId,
        text: selections.map((selection) => selection.text).filter(Boolean).join(', '),
        answerIds: selections.map((selection) => selection.answerId).filter(Boolean),
        selections
      };
      if (!lessonElementState.currentResponseAnswer.userInputMap) {
        const userInputMap = questionService.updateUserInputMap({
          model,
          lessonElementState
        });
        userInputMap.set(dataId, payload);
      } else {
        lessonElementState.currentResponseAnswer.userInputMap.set(dataId, payload);
      }
    } else {
      const answerId = questionService.getPickAnswerId(data);
      const payload = {
        ...data.originalData,
        dataId,
        answerId,
        text: (model?.responseFormat === 'html') ? data.viewLabel : data.label,
      };
      if (!lessonElementState.currentResponseAnswer.userInputMap) {
        const userInputMap = questionService.updateUserInputMap({
          model,
          lessonElementState
        });
        userInputMap.set(dataId, payload);
      } else {
        lessonElementState.currentResponseAnswer.userInputMap.set(dataId, payload);
      }
    }
    lessonManager.stampResponsePublisherTranslationId(lessonElementState?.currentResponseAnswer);
  }

  setCorrectAnswer = (state, model) => {
    const userInputMap = new Map();
    for (const modelValidationPrompt of (model?.validation?.prompts || [])) {
      if (modelValidationPrompt && modelValidationPrompt.rangeType === 'clozeMultichoice') {
        const correctAnswers = (modelValidationPrompt.answers || []).filter((answer) => answer.isCorrect);
        const isMultiSelect = !!modelValidationPrompt.multiSelect
          || promptService.isMultiSelectDropdownPrompt({ dataId: modelValidationPrompt.id, model });
        if (isMultiSelect) {
          userInputMap.set(modelValidationPrompt.id, {
            dataId: modelValidationPrompt.id,
            text: correctAnswers.map((answer) => answer.text).filter(Boolean).join(', '),
            answerIds: correctAnswers.map((answer) => answer.id).filter(Boolean),
            selections: correctAnswers.map((answer) => ({
              answerId: answer.id,
              text: answer.text
            })),
            isCorrect: true
          });
        } else {
          const correctAnswer = correctAnswers[0];
          userInputMap.set(modelValidationPrompt.id, {
            dataId: modelValidationPrompt.id,
            answerId: correctAnswer?.id,
            text: correctAnswer ? correctAnswer.text : '',
            isCorrect: true
          });
        }
      } else if (modelValidationPrompt && modelValidationPrompt.rangeType === 'cloze') {
        let answerText;
        if (promptService.isUserInputAlwaysCorrectIfFoundInModelPromptAnswers(model, modelValidationPrompt)) {
          answerText = modelValidationPrompt?.answers?.[0]?.text || '';
        } else {
          answerText = modelValidationPrompt?.answers?.find((answer) => answer.isCorrect)?.text || '';
        }
        if (answerText && model?.responseFormat === 'text') {
          answerText = utilsService.getPlainTextAnswerDisplay(model, answerText);
        }

        if (answerText && ((model && !utilsService.isNullOrEmpty(model.mathText)) || model?.responseFormat === 'math')) {
          answerText = renderToStaticMarkup(parse(answerText, {
            replace: (domNode) => clozeMathService.appendDataIdToMathDomNodeThenConvertToMencloseIfApplicable(domNode, modelValidationPrompt.id, model)
          }));
        }
        const dataId = modelValidationPrompt?.id;
        userInputMap.set(dataId, {
          dataId,
          text: answerText || (model.mathText && renderToStaticMarkup(
            clozeMathService.renderClozeMathBlankPlaceholder(dataId, model, '?')
          )) || '',
          isCorrect: true
        });
      }
    }

    state.setCurrentResponse(utilsService.safeMobxClone({
      userInputMap, lessonElementId: model.lessonElementId
    }));
  }

  choiceTextsMatch = (left, right, model) => {
    const normalize = (value) => {
      const { stripZeroWidthChars, stripTagsAndEntities, stripWrappingParagraphTags } = utilsService;
      return stripTagsAndEntities(stripZeroWidthChars(stripWrappingParagraphTags(value || ''))).trim();
    };
    let normalizedLeft = normalize(left);
    let normalizedRight = normalize(right);
    if (model?.isCaseSensitive === false) {
      normalizedLeft = normalizedLeft.toLowerCase();
      normalizedRight = normalizedRight.toLowerCase();
    }
    return !!normalizedLeft && normalizedLeft === normalizedRight;
  }

  isComboChoiceSelected = (userInput, answerId, prompt, optionText, model) => {
    if (!userInput) {
      return false;
    }
    if (answerId && (userInput.answerId === answerId || (userInput.answerIds || []).includes(answerId))) {
      return true;
    }
    const targetText = optionText || (prompt?.answers || []).find((answer) => answer?.id === answerId)?.text;
    const textMatches = (value) => !!targetText && this.choiceTextsMatch(value, targetText, model);
    if ((userInput.selections || []).some((selection) => (
      (answerId && selection?.answerId === answerId) || textMatches(selection?.text)
    ))) {
      return true;
    }
    if (typeof userInput.text === 'string' && targetText) {
      return userInput.text.split(/\s*,\s*/).some((part) => textMatches(part));
    }
    return false;
  }

  getMultiSelectPromptForChoice = (answerId, model) => {
    if (!answerId || !model) {
      return null;
    }
    const prompts = model.validation?.prompts || [];
    return prompts.find((prompt) => (
      prompt?.rangeType === 'clozeMultichoice' &&
      (prompt.multiSelect || promptService.isMultiSelectDropdownPrompt({ dataId: prompt.id, model })) &&
      (prompt.answers || []).some((answer) => answer.id === answerId)
    )) || null;
  }

  isMultiSelectBlankCorrect = (prompt, lessonElementState) => {
    const response = lessonElementState?.currentResponseAnswer;
    // Show Correct Answer replaces the blanks, then rescoring writes the student's
    // original answerResults back onto that response. Ignore those while the
    // correct answers are on screen.
    if (!lessonElementState?.correctAnswerShowing) {
      const answerResult = response?.answerResults?.find((result) => (
        result.answerId === prompt.id && !result.cell
      ));
      if (answerResult && answerResult.match !== undefined) {
        return !!answerResult.match;
      }
    }

    const answers = prompt?.answers || [];
    let correctCount = 0;
    let selectedCorrect = 0;
    let selectedIncorrect = 0;
    answers.forEach((answer) => {
      const selected = this.isComboChoiceSelected(response?.userInputMap?.get(prompt.id), answer.id, prompt);
      if (answer.isCorrect) {
        correctCount += 1;
        if (selected) {
          selectedCorrect += 1;
        }
      } else if (selected) {
        selectedIncorrect += 1;
      }
    });
    if (correctCount < 1) {
      return false;
    }
    return selectedCorrect === correctCount && selectedIncorrect === 0;
  }

  /**
   * Student dropdown options are plain labels, while scoring lists store
   * validation answer ids. Match a menu choice back to its blank by id or text.
   */
  findMultiSelectDropdownChoice = (dataId, model, lessonElementState, promptId) => {
    if (!dataId || !model) {
      return null;
    }
    const blankIds = new Set();
    (model.validation?.prompts || []).forEach((prompt) => prompt?.id && blankIds.add(prompt.id));
    (model.prompts || []).forEach((prompt) => prompt?.id && blankIds.add(prompt.id));
    (model.responseItems || []).forEach((item) => item?.promptId && blankIds.add(item.promptId));
    if (blankIds.has(dataId)) {
      return null;
    }

    const prompts = [];
    const considerPrompt = (prompt) => {
      if (!prompt?.id || prompts.some((existing) => existing.id === prompt.id)) {
        return;
      }
      if (prompt.rangeType && prompt.rangeType !== 'clozeMultichoice') {
        return;
      }
      if (!(prompt.multiSelect || promptService.isMultiSelectDropdownPrompt({ dataId: prompt.id, model }))) {
        return;
      }
      prompts.push(prompt);
    };
    (model.validation?.prompts || []).forEach(considerPrompt);
    (model.prompts || []).forEach(considerPrompt);
    (model.responseItems || []).forEach((item) => {
      if (!promptService.isMultiSelectDropdownPrompt({ dataId: item?.promptId, model })) {
        return;
      }
      considerPrompt({
        id: item.promptId,
        answers: item.responseOptions,
        rangeType: 'clozeMultichoice',
        multiSelect: true
      });
    });

    const matches = [];
    prompts.forEach((prompt) => {
      (prompt.answers || []).forEach((answer) => {
        if (typeof answer === 'string') {
          if (this.choiceTextsMatch(answer, dataId, model)) {
            matches.push({ prompt, answerId: null, optionText: answer });
          }
          return;
        }
        if (answer?.id === dataId) {
          matches.push({ prompt, answerId: answer.id, optionText: answer.text });
          return;
        }
        if (answer?.text && this.choiceTextsMatch(answer.text, dataId, model)) {
          matches.push({ prompt, answerId: answer.id || null, optionText: answer.text });
        }
      });
    });
    const scopedMatches = promptId ? matches.filter((match) => match.prompt?.id === promptId) : matches;
    const choiceMatches = scopedMatches.length ? scopedMatches : matches;
    if (!choiceMatches.length) {
      return null;
    }
    const userInputMap = lessonElementState?.currentResponseAnswer?.userInputMap;
    return choiceMatches.find((match) => this.isComboChoiceSelected(
      userInputMap?.get(match.prompt.id),
      match.answerId,
      match.prompt,
      match.optionText,
      model
    )) || choiceMatches[0];
  }

  findSelectedChoiceResult = (response, prompt, answerId, optionText, model) => {
    return (response?.answerResults || []).find((result) => {
      if (!result || result.match === undefined) {
        return false;
      }
      // Blank-level rows are keyed by the prompt id and do not describe one choice.
      if (result.answerId === prompt?.id && !result.text) {
        return false;
      }
      if (result.cell && prompt?.id && result.cell !== prompt.id) {
        return false;
      }
      if (answerId && result.answerId === answerId && result.answerId !== prompt?.id) {
        return true;
      }
      return !!(optionText && result.text && this.choiceTextsMatch(result.text, optionText, model));
    }) || null;
  }

  isMultiSelectChoiceCorrect = (answerId, prompt, lessonElementState, optionText, model) => {
    const response = lessonElementState?.currentResponseAnswer;
    if (!lessonElementState?.correctAnswerShowing) {
      if (answerId && response?.trueCorrectList?.includes(answerId)) {
        return true;
      }
      if (answerId && response?.selectedIncorrectList?.includes(answerId)) {
        return false;
      }
      if (answerId && response?.omittedList?.includes(answerId)) {
        return null;
      }
    }

    const userInput = response?.userInputMap?.get(prompt.id);
    const selected = this.isComboChoiceSelected(userInput, answerId, prompt, optionText, model);
    // Unselected choices stay unmarked. An incorrect icon is only for a choice
    // the student actually submitted.
    if (!selected) {
      return null;
    }

    const validationAnswer = (prompt.answers || []).find((answer) => (
      answer && typeof answer === 'object' && (
        (answerId && answer.id === answerId) ||
        (optionText && this.choiceTextsMatch(answer.text, optionText, model))
      )
    ));
    if (validationAnswer?.isCorrect === true) {
      return true;
    }
    if (validationAnswer?.isCorrect === false) {
      return false;
    }

    const choiceResult = this.findSelectedChoiceResult(response, prompt, answerId, optionText, model);
    if (choiceResult) {
      return !!choiceResult.match;
    }
    return null;
  }

  isAnswerCorrect = (dataId, lessonElementState, model, _isPracticeRunning, promptId) => {
    const response = lessonElementState?.currentResponseAnswer;
    const showingCorrectAnswer = !!lessonElementState?.correctAnswerShowing;
    if (!showingCorrectAnswer) {
      if (response?.trueCorrectList?.includes(dataId)) {
        return true;
      }
      if (response?.selectedIncorrectList?.includes(dataId)) {
        return false;
      }
      if (response?.omittedList?.includes(dataId)) {
        return null;
      }
    }

    const multiSelectPrompt = this.getMultiSelectPromptForChoice(dataId, model);
    if (multiSelectPrompt) {
      const validationAnswer = (multiSelectPrompt.answers || []).find((answer) => answer?.id === dataId);
      return this.isMultiSelectChoiceCorrect(
        dataId,
        multiSelectPrompt,
        lessonElementState,
        validationAnswer?.text,
        model
      );
    }

    const userInputMap = response?.userInputMap || questionService.updateUserInputMap({
      model,
      responseAnswer: lessonElementState.currentResponseAnswer,
      lessonElementState
    });
    const currentUserInput = userInputMap?.get(dataId);

    const prompt = promptService.getCurrentPrompt({
      dataId,
      lessonElementState,
      model,
      promptType: 'anyModelPrompt'
    });
    
    if (prompt?.rangeType === 'cloze') {
      if (!showingCorrectAnswer && lessonElementState?.currentResponseAnswer?.answerResults) {
        const matchingResults = lessonElementState.currentResponseAnswer.answerResults
          .filter(result => result.answerId === dataId && result.match !== undefined);
        if (matchingResults.length) {
          return matchingResults.some(result => result.match);
        }
      }
    } else if (prompt?.rangeType === 'clozeMultichoice') {
      if (!showingCorrectAnswer && lessonElementState?.currentResponseAnswer?.answerResults) {
        const answerResult = lessonElementState.currentResponseAnswer.answerResults.find((result) => (
          result.answerId === dataId && !result.cell
        ));
        if (answerResult && answerResult.match !== undefined) {
          return answerResult.match;
        }
      }
      if (prompt.multiSelect || promptService.isMultiSelectDropdownPrompt({ dataId, model })) {
        return this.isMultiSelectBlankCorrect(prompt, lessonElementState);
      }
    }
    
    // A dropdown choice that is not a blank id must not fall through to the
    // generic "not found" check, which marks every option incorrect.
    if (!prompt || prompt.id !== dataId) {
      const dropdownChoice = this.findMultiSelectDropdownChoice(dataId, model, lessonElementState, promptId);
      if (dropdownChoice) {
        return this.isMultiSelectChoiceCorrect(
          dropdownChoice.answerId,
          dropdownChoice.prompt,
          lessonElementState,
          dropdownChoice.optionText || dataId,
          model
        );
      }
    }

    // Fallback to existing logic for backward compatibility
    // Stripping paragraph tags here so we can trim the string.
    const currentUserInputValue = currentUserInput?.text;

    const result = currentUserInput?.isCorrect || promptService.isCorrectPromptAnswerStatus({
      dataId, currentUserInput, currentUserInputValue, model
    });
    return result;
  }

  resetStudentAnswer = (state, _validation) => {
    state.setCurrentResponse(utilsService.safeMobxClone(state.cachedResponseAnswer));
  }
}

export default new ClozeComboBehavior();
